import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

export function wrapLabel(text: string, maxCharsPerLine = 14): string[] {
  const sanitized = text.trim();
  if (!sanitized) {
    return [""];
  }

  const words = sanitized.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const tentative = current.length ? `${current} ${word}` : word;
    if (tentative.length <= maxCharsPerLine || !current.length) {
      current = tentative;
    } else {
      lines.push(current);
      current = word;
      if (lines.length === 1) {
        continue;
      }
    }
    if (lines.length === 1 && current.length > maxCharsPerLine) {
      lines.push(current);
      current = "";
      break;
    }
  }

  if (current) {
    lines.push(current);
  }

  if (lines.length > 2) {
    const [first, ...rest] = lines;
    lines.length = 0;
    lines.push(first, rest.join(" "));
  }

  return lines;
}

export function exportSvgToPng(
  svg: SVGSVGElement,
  filename: string,
  background = "#0e0f12"
): void {
  const serializer = new window.XMLSerializer();
  const xml = serializer.serializeToString(svg);
  const svgBlob = new window.Blob([xml], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = window.URL.createObjectURL(svgBlob);
  const image = new window.Image();

  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = svg.viewBox.baseVal.width || svg.clientWidth;
    canvas.height = svg.viewBox.baseVal.height || svg.clientHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      window.URL.revokeObjectURL(url);
      return;
    }

    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    window.URL.revokeObjectURL(url);

    const anchor = document.createElement("a");
    anchor.download = filename;
    anchor.href = canvas.toDataURL("image/png");
    anchor.click();
  };

  image.src = url;
}

interface DiagramNodePillProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  textColor: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  shadowId?: string;
  radius?: number;
  lineSpacing?: number;
  maxCharsPerLine?: number;
}

export function DiagramNodePill({
  x,
  y,
  width,
  height,
  fill,
  stroke,
  textColor,
  label,
  icon,
  href,
  shadowId,
  radius = 24,
  lineSpacing = 18,
  maxCharsPerLine = 16,
}: DiagramNodePillProps): ReactElement {
  const lines = wrapLabel(label, maxCharsPerLine);
  const labelX = x + (icon ? 48 : 24);
  const baseline = y + height / 2 - ((lines.length - 1) * lineSpacing) / 2;
  const content = (
    <g style={{ cursor: href ? "pointer" : "default" }}>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={radius}
        ry={radius}
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
        filter={shadowId ? `url(#${shadowId})` : undefined}
      />
      {icon && (
        <g
          transform={`translate(${x + 20}, ${y + height / 2 - 10})`}
          pointerEvents="none"
        >
          {isValidElement(icon)
            ? cloneElement(icon as ReactElement, {
                width: 20,
                height: 20,
                stroke: (icon as ReactElement).props?.stroke ?? 2,
                color: (icon as ReactElement).props?.color ?? textColor,
              })
            : icon}
        </g>
      )}
      <text
        x={labelX}
        y={baseline}
        fill={textColor}
        fontSize={14}
        fontWeight={600}
        textAnchor="start"
        dominantBaseline="middle"
        pointerEvents="none"
      >
        {lines.map((line, index) => (
          <tspan
            key={`${line}-${index}`}
            x={labelX}
            dy={index === 0 ? 0 : lineSpacing}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return content;
}
